// @ts-strict-ignore
import { SnackBarProvider, TooltipProvider } from '@usealloy/component-library';
import { Provider as ReduxStoreProvider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';

import CustomerAccessBanner from '~/components/CustomerAccessBanner';
import { ModalProvider } from '~/journey/ModalContext';
import { NotificationsProvider } from '~/dashboard/components/notifications/NotificationsContext';
import store from '~/store';
import { useMessages } from '~/helpers/hooks';

import {
  AlertColorContext,
  AuthContext,
  EntityProvider,
  MessageContext,
  UserContext,
} from './context';
import type { EventAllEventTypesAndStatuses } from './context';
import { queryClient } from './config';
import { ThemeProvider } from '~/dashboard/ThemeProvider/ThemeProvider';
import { themes } from './ThemeProvider/constants';
import type { Themes } from './ThemeProvider/constants';
import { useAlertColors, useAuthContext } from './hooks';
import { PageTitleProvider } from './PageTitleProvider';
import { APIProvider } from '~/helpers/api-new/context';
import EvaluationExplainabilityContextProvider from '~/investigations/context/EvaluationExplainabilityContext';

type Props = {
  alloyEnvironment: string;
  children: JSX.Element;
  isAuthenticated: boolean;
  theme?: Themes;
};

function AppProvider(props: Props) {
  const {
    alloyEnvironment,
    children,
    isAuthenticated,
    theme = themes.light,
  } = props;

  const {
    authContext,
    getUserContext,
    setContext,
    userContext: context,
  } = useAuthContext({ alloyEnvironment, isAuthenticated });

  const updateBreadcrumbs = useCallback(
    breadcrumbs => {
      if (breadcrumbs && Array.isArray(breadcrumbs)) {
        setContext(prev => ({ ...prev, breadcrumbs }));
      }
    },
    [setContext],
  );

  const [isInformationalView, setIsInformationalView] = useState<boolean>(false);
  const [events, setEvents] = useState<EventAllEventTypesAndStatuses[]>([]);

  const userContextObj = useMemo(
    () => ({
      ...context,
      updateBreadcrumbs,
      getUserContext,
      isInformationalView,
      setIsInformationalView,
      events,
      setEvents,
    }),
    [
      context,
      updateBreadcrumbs,
      getUserContext,
      isInformationalView,
      setIsInformationalView,
      events,
      setEvents,
    ],
  );

  const messageContextObj = useMessages();

  const {
    customer: { organizationName },
  } = context;
  const { alertColors } = useAlertColors({ isAuthenticated, organizationName });

  return (
    <ReduxStoreProvider store={store}>
      <ThemeProvider theme={theme}>
        <div id="alloy-overlay-root" />
        <QueryClientProvider client={queryClient}>
          <UserContext.Provider value={userContextObj}>
            <AuthContext.Provider value={authContext}>
              <PageTitleProvider>
                {/* CustomerAccessBanner makes use of UserContext and AuthContext */}
                <CustomerAccessBanner />
                <EntityProvider>
                  <SnackBarProvider>
                    <APIProvider>
                      <MessageContext.Provider value={messageContextObj}>
                        <NotificationsProvider
                          isAuthenticated={isAuthenticated}
                        >
                          <ModalProvider>
                            <AlertColorContext.Provider value={alertColors}>
                              <EvaluationExplainabilityContextProvider>
                                <TooltipProvider>{children}</TooltipProvider>
                              </EvaluationExplainabilityContextProvider>
                            </AlertColorContext.Provider>
                          </ModalProvider>
                        </NotificationsProvider>
                      </MessageContext.Provider>
                    </APIProvider>
                  </SnackBarProvider>
                </EntityProvider>
              </PageTitleProvider>
            </AuthContext.Provider>
          </UserContext.Provider>
        </QueryClientProvider>
      </ThemeProvider>
    </ReduxStoreProvider>
  );
}

export default AppProvider;
